import { useMutation } from '@tanstack/react-query'

import type { IHttpError, IResponse } from '../core'
import { Model } from '../core'
import * as yup from 'yup'
import type { IUser, UserTypeEnum } from './user'

export interface ILoginPayload {
  email: string
  password: string
  remember: boolean
}

export interface ILoginResponse {
  accessToken: string
  expiresIn: number
  refreshToken: string
  two_fa_otp: boolean
  password: boolean
  email: boolean
  user: Partial<IUser>
}

export interface IVerifyUserInformationResponse {
  image: string
  emailVerified: boolean
  phoneVerified: boolean
}

export interface IVerifyUserInformationOTPResponse {
  codeVerified: boolean
}

export enum NotificationChannel {
  Telegram = 'telegram',
  Email = 'email',
  Whatsapp = 'whatsapp'
}

export interface RegisterPayload {
  // Step 1: Information
  branch_id?: number
  role_id?: number
  agent_id: string
  name: string
  country: string
  passport: string
  hand_card: string
  channel: string
  phone_code: string
  phone: string
  email: string
  date?: string

  first_name: string
  employee_id: string
  company: string
  company_id: number
  birth_date: string

  // Step 3: Signature
  signature: string
  avatar: File
  passport_front: File
  passport_back: File

  // Step 4: Password
  password: string
  confirm_password: string

  // Step 5: 2FA
  two_fa_otp: string
  user_type: UserTypeEnum
  token: string
  sub_branch_master_id: number
}

export interface IRegisterResponse {
  accessToken: string
  otpAuthUrl: string
}

export interface IResetPasswordPayload {
  email: string
  verification_code?: string
  password?: string
  confirm_password?: string
  token?: string
}

export interface IResetPasswordResponse {
  email: string
  token: string
}

export interface IChangePasswordPayload {
  old_password: string
  password: string
  password_confirm: string
}

export interface IToggleTwoFactorPayload {
  is_two_fa_enabled: boolean
  otpAuthUrl: string
}

export class Auth extends Model {
  static login(payload: ILoginPayload) {
    return this.api.post<IResponse<ILoginResponse>>({
      url: '/auth/login',
      data: payload
    })
  }

  static getMe() {
    return this.api
      .get<IResponse<IUser>>({
        url: '/auth/profile'
      })
      .then(r => r.data)
  }

  static updateProfile(payload: Partial<IUser>) {
    return this.api.put<IResponse<IUser>>({
      url: '/auth/profile',
      data: payload
    })
  }

  static activeAccount(token: string) {
    return this.api
      .get<IResponse<IUser>>({
        url: '/auth/activate-account',
        params: {
          token
        }
      })
      .then(r => r.data.data)
  }

  static forgotPassword(email: string) {
    return this.api.put<IResponse<IResetPasswordResponse>>({
      url: '/auth/forgot-password',
      data: {
        email
      }
    })
  }

  static verifyForgotPassword(payload: IResetPasswordPayload) {
    return this.api.put<IResponse<IResetPasswordResponse>>({
      url: '/auth/verify-forgot-password',
      data: payload
    })
  }

  static verifyForgot2FA(payload: IResetPasswordPayload) {
    return this.api.put<IResponse<IRegisterResponse>>({
      url: '/auth/verify-forgot-2FA',
      data: payload
    })
  }

  static resetPassword(payload: IResetPasswordPayload) {
    return this.api.put<IResponse<IResetPasswordResponse>>({
      url: '/auth/reset-password',
      data: payload
    })
  }

  static verifyRegisterInformation(payload: RegisterPayload) {
    return this.api.post<IResponse<IVerifyUserInformationResponse>>({
      url: '/auth/verify-information',
      data: payload
    })
  }

  static verifyRegisterInformationOTP(payload: RegisterPayload) {
    return this.api.post<IResponse<ILoginResponse>>({
      url: '/auth/verify-information-otp',
      data: payload
    })
  }

  static register(payload: RegisterPayload) {
    return this.api.postFormData<IResponse<IRegisterResponse>>({
      url: '/auth/register',
      data: payload
    })
  }

  static finalRegisterWith2fa(payload: Partial<RegisterPayload>) {
    return this.api.post<IResponse<ILoginResponse>>({
      url: '/auth/authenticate',
      data: payload,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
      }
    })
  }

  static finalForgot2fa(payload: Partial<RegisterPayload>) {
    return this.api.post<IResponse<void>>({
      url: '/auth/authenticate-forgot-2fa',
      data: payload,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
      }
    })
  }

  static finishRegisterInternalUser(payload: Partial<RegisterPayload>) {
    return this.api.post<IResponse<ILoginResponse>>({
      url: '/auth/finish-activate-account',
      data: payload
    })
  }

  static changePassword(payload: IChangePasswordPayload) {
    return this.api.put<IResponse<any>>({
      url: '/auth/change-password',
      data: payload
    })
  }

  static logout() {
    return this.api.get({
      url: 'auth/logout'
    })
  }

  static toggleTwoFactor(payload: { is_two_fa_enabled: boolean }) {
    return this.api.put<IResponse<IUser>>({
      url: '/auth/toggle-two-factor',
      data: payload
    })
  }

  static verifyToggleTwoFactor(payload: { is_two_fa_enabled: boolean; two_fa_otp: string }) {
    return this.api.put<IResponse<IUser>>({
      url: '/auth/verify-toggle-two-factor',
      data: payload
    })
  }

  static resetTwoFactor(payload: { verification_code: string }) {
    return this.api.put<IResponse<IUser>>({
      url: '/auth/reset-two-factor',
      data: payload
    })
  }

  static togglePin(payload: { pin: string }) {
    return this.api.put<IResponse<IUser>>({
      url: `/auth/toggle-pin`,
      data: {
        pin: payload.pin
      }
    })
  }

  static resetPin(payload: { verification_code: string }) {
    return this.api.put<IResponse<IUser>>({
      url: '/auth/reset-pin',
      data: payload
    })
  }

  static getLoginSchemaValidation(__: (key: string) => string) {
    return yup.object().shape({
      email: yup.string().required(__('Username is required')),
      password: yup.string().required(__('Password is required')).min(6, __('Password is min 6 character')),
      remember: yup.boolean().default(false)
    })
  }

  static getLoginOTPSchemaValidation(__: (key: string) => string) {
    return yup.object().shape({
      two_fa_otp: yup
        .string()
        .required(__('Authentication Code is required'))
        .min(6, __('Authentication Code must be 6 characters'))
        .max(6, __('Authentication Code must be 6 characters'))
    })
  }

  static handleLoginBefore2FA(response: any) {
    localStorage.setItem('accessToken', response.accessToken)
    localStorage.setItem('refreshToken', response.refreshToken)
  }

  static handleLoginSuccess(response: ILoginResponse) {
    localStorage.removeItem('accessToken')
    localStorage.setItem('accessToken', response.accessToken)

    const expiresTime = {
      value: response.expiresIn,
      expiresAt: Date.now() + response.expiresIn * 1000
    }

    localStorage.setItem('expiresIn', JSON.stringify(expiresTime))

    if (response.refreshToken) localStorage.setItem('refreshToken', response.refreshToken)
  }

  static handleLogout() {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('expiresIn')
    localStorage.removeItem('refreshToken')
  }

  static changeLanguage(language: string) {
    localStorage.setItem('i18nextLng', language)
    window.location.reload()
  }

  static checkValidToken() {
    const token = localStorage.getItem('expiresIn')

    if (!token) return null

    const item = JSON.parse(token)
    const now = Date.now()

    if (now > item.expiresAt) {
      this.handleLogout()

      return null
    }

    return item.value
  }
}

Auth.setup()

export function useLoginMutation() {
  return useMutation<IResponse<ILoginResponse>, IHttpError, ILoginPayload>({
    mutationFn: payload => {
      return Auth.login(payload).then(r => r.data)
    }
  })
}

export function useVerifyRegisterInformationMutation() {
  return useMutation<IResponse<IVerifyUserInformationResponse>, IHttpError, RegisterPayload>({
    mutationFn: payload => {
      return Auth.verifyRegisterInformation(payload).then(r => r.data)
    }
  })
}

export function useRegisterMutation() {
  return useMutation<IResponse<IRegisterResponse>, IHttpError, RegisterPayload>({
    mutationFn: payload => {
      return Auth.register(payload).then(r => r.data)
    }
  })
}

export function useFinalRegisterMutation() {
  return useMutation<IResponse<ILoginResponse>, IHttpError, Partial<RegisterPayload>>({
    mutationFn: payload => {
      return Auth.finalRegisterWith2fa(payload).then(r => r.data)
    }
  })
}

export function useFinalForgot2FAMutation() {
  return useMutation<IResponse<void>, IHttpError, Partial<RegisterPayload>>({
    mutationFn: payload => {
      return Auth.finalForgot2fa(payload).then(r => r.data)
    }
  })
}

export function useFinishRegisterMutation() {
  return useMutation<IResponse<ILoginResponse>, IHttpError, Partial<RegisterPayload>>({
    mutationFn: payload => {
      return Auth.finishRegisterInternalUser(payload).then(r => r.data)
    }
  })
}

export function useVerifyRegisterInformationOTPMutation() {
  return useMutation<IResponse<ILoginResponse>, IHttpError, RegisterPayload>({
    mutationFn: payload => {
      return Auth.verifyRegisterInformationOTP(payload).then(r => r.data)
    }
  })
}

export function useForgotPasswordMutation() {
  return useMutation<IResponse<IResetPasswordResponse>, IHttpError, IResetPasswordPayload>({
    mutationFn: ({ email }) => Auth.forgotPassword(email).then(r => r.data)
  })
}

export function useVerifyForgotPasswordMutation() {
  return useMutation<IResponse<IResetPasswordResponse>, IHttpError, IResetPasswordPayload>({
    mutationFn: payload => Auth.verifyForgotPassword(payload).then(r => r.data)
  })
}

export function useVerifyForgot2FAMutation() {
  return useMutation<IResponse<IRegisterResponse>, IHttpError, IResetPasswordPayload>({
    mutationFn: payload => Auth.verifyForgot2FA(payload).then(r => r.data)
  })
}

export function useResetPasswordMutation() {
  return useMutation<IResponse<IResetPasswordResponse>, IHttpError, IResetPasswordPayload>({
    mutationFn: payload => Auth.resetPassword(payload).then(r => r.data)
  })
}

export function useChangePasswordMutation() {
  return useMutation<IResponse<any>, IHttpError, IChangePasswordPayload>({
    mutationFn: payload => Auth.changePassword(payload).then(r => r.data)
  })
}

export function useLogoutMutation() {
  return useMutation<void, IHttpError>({
    mutationFn: () => {
      return Auth.logout().then(() => {})
    }
  })
}

export function useToggleTwoFactorMutation() {
  return useMutation<IResponse<IToggleTwoFactorPayload>, IHttpError, { is_two_fa_enabled: boolean }>({
    mutationFn: payload => Auth.toggleTwoFactor(payload).then(r => r.data)
  })
}

export function useVerifyToggleTwoFactorMutation() {
  return useMutation<IResponse<any>, IHttpError, { is_two_fa_enabled: boolean; two_fa_otp: string }>({
    mutationFn: payload => Auth.verifyToggleTwoFactor(payload).then(r => r.data)
  })
}

export function useResetTwoFactorMutation() {
  return useMutation<IResponse<IUser>, IHttpError, { verification_code: string }>({
    mutationFn: payload => Auth.resetTwoFactor(payload).then(r => r.data)
  })
}

export function useTogglePinMutation() {
  return useMutation<IResponse<IUser>, IHttpError, { pin: string }>({
    mutationFn: payload => Auth.togglePin(payload).then(r => r.data)
  })
}

export function useResetPinMutation() {
  return useMutation<IResponse<IUser>, IHttpError, { verification_code: string }>({
    mutationFn: payload => Auth.resetPin(payload).then(r => r.data)
  })
}

export function useUpdateProfileMutation() {
  return useMutation<IResponse<IUser>, IHttpError, Partial<IUser>>({
    mutationFn: payload => Auth.updateProfile(payload).then(r => r.data)
  })
}
